/**
 * API client for the Comment Analysis Artifact (proceedings-page chat).
 *
 * Endpoints:
 *  - POST /api/chat/artifact/{docket_id}/refresh
 *  - GET  /api/chat/artifact/{docket_id}/status
 *  - POST /api/chat/artifact/{docket_id}/ask
 *
 * Architecture documented in ByteRover commsgenie space at
 * architecture/comment_analysis/comment_analysis_artifact_architecture.md
 */

export type CompileStatus = "queued" | "running" | "fresh" | "failed";
export type RefreshStatus = "fresh" | "queued" | "running" | "deferred";

export interface CompileJob {
  id: string;
  docket_number: string;
  status: CompileStatus;
  started_at: string | null;
  completed_at: string | null;
  comments_processed: number | null;
  new_comments_processed: number | null;
  arguments_extracted: number | null;
  authorities_extracted: number | null;
  hot_issues_count: number | null;
  coalitions_count: number | null;
  cost_total_usd: number | null;
  error: string | null;
  created_at: string;
}

export interface RefreshResponse {
  status: RefreshStatus;
  message?: string;
  job_id?: string;
  drift_n?: number;
  compiled_at?: string;
  comments_in_artifact?: number;
}

export interface ArtifactStatus {
  docket_number: string;
  has_artifact: boolean;
  compiled_at: string | null;
  comments_in_artifact: number;
  comments_in_supabase: number;
  drift_n: number;
  active_compile_job: CompileJob | null;
  last_failed_job: CompileJob | null;
}

export interface Citation {
  submission_id: string;
  filer_name: string;
  page: number;
  quote: string;
  extraction_id?: string;
}

export type AnswerBlockKind =
  | "text"
  | "extraction"
  | "rollup"
  | "comparison"
  | "synthesis"      // argument_synthesis output
  | "gap"
  | "list";

export interface AnswerBlock {
  kind: AnswerBlockKind;
  content: string;
  citations: Citation[];
  metadata: Record<string, unknown>;
}

export interface ChatAgentResponse {
  answer_blocks: AnswerBlock[];
  citations: Citation[];
  confidence_summary: "high" | "mixed" | "low";
  unsupported_claims: string[];
  intent: string | null;
  fast_path: boolean;
}

export interface AskRequest {
  question: string;
  history?: Array<{ role: string; content: string }>;
}

// ----- ENV / base URL ---------------------------------------------------

function getBaseUrl(): string {
  const raw = (import.meta as any)?.env?.VITE_RAG_API_BASE_URL as
    | string
    | undefined;
  const fallback = "https://fcc-summarize-app-r4ac3.ondigitalocean.app";
  return (raw || fallback).replace(/\/+$/, "");
}

async function parseErrorMessage(res: Response): Promise<string> {
  const contentType = res.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    const data = await res.json().catch(() => null as any);
    if (data?.detail) return String(data.detail);
    if (data?.error) return String(data.error);
    return `HTTP ${res.status}: ${res.statusText}`;
  }
  const text = await res.text().catch(() => "");
  return text?.trim() ? text.trim() : `HTTP ${res.status}: ${res.statusText}`;
}

// ----- API calls --------------------------------------------------------

/** Trigger a refresh-on-visit. Cheap path returns immediately when nothing
 * new since last compile; otherwise enqueues a background job. */
export async function refreshArtifact(
  docketId: string,
): Promise<RefreshResponse> {
  const url = `${getBaseUrl()}/api/chat/artifact/${encodeURIComponent(docketId)}/refresh`;
  const res = await fetch(url, { method: "POST" });
  if (!res.ok) throw new Error(await parseErrorMessage(res));
  return (await res.json()) as RefreshResponse;
}

/** Poll for compile status / freshness. */
export async function getArtifactStatus(
  docketId: string,
): Promise<ArtifactStatus> {
  const url = `${getBaseUrl()}/api/chat/artifact/${encodeURIComponent(docketId)}/status`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(await parseErrorMessage(res));
  return (await res.json()) as ArtifactStatus;
}

/** Ask the comment-analysis agent a question. Privacy: this endpoint
 * does NOT log message content. If your question contains a draft
 * argument, the body is processed but not persisted to chat history. */
export async function askArtifact(
  docketId: string,
  request: AskRequest,
): Promise<ChatAgentResponse> {
  const url = `${getBaseUrl()}/api/chat/artifact/${encodeURIComponent(docketId)}/ask`;
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
  });
  if (!res.ok) throw new Error(await parseErrorMessage(res));
  return (await res.json()) as ChatAgentResponse;
}


// ----- streaming ask -----------------------------------------------------

export type StreamEventKind = "intent" | "prelude" | "chunk" | "done" | "error";

export interface StreamHandlers {
  onIntent?: (data: { intent: string; confidence?: number; fast_path: boolean }) => void;
  onPrelude?: (data: Record<string, unknown>) => void;
  onChunk?: (delta: string) => void;
  onDone?: (response: ChatAgentResponse) => void;
  onError?: (detail: string) => void;
}

/**
 * Streaming variant of askArtifact using Server-Sent Events.
 *
 * For argument_synthesis the LLM prose streams chunk-by-chunk via the
 * `chunk` event so the UI can render text as it generates. Fast-path
 * intents go intent → done without chunks. All intents end with a
 * `done` event carrying the complete ChatAgentResponse.
 *
 * Caller passes handlers; this resolves when the stream ends. Aborts
 * on AbortSignal cancellation.
 */
export async function askArtifactStream(
  docketId: string,
  request: AskRequest,
  handlers: StreamHandlers,
  signal?: AbortSignal,
): Promise<void> {
  const url = `${getBaseUrl()}/api/chat/artifact/${encodeURIComponent(docketId)}/ask/stream`;
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
    signal,
  });
  if (!res.ok || !res.body) {
    const msg = await parseErrorMessage(res);
    handlers.onError?.(msg);
    throw new Error(msg);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // Split on SSE record separator (double newline). Keep trailing
    // partial record in the buffer for the next iteration.
    const records = buffer.split("\n\n");
    buffer = records.pop() ?? "";
    for (const record of records) {
      if (!record.trim()) continue;
      const lines = record.split("\n");
      let event = "message";
      let dataLine = "";
      for (const ln of lines) {
        if (ln.startsWith("event: ")) event = ln.slice(7).trim();
        else if (ln.startsWith("data: ")) dataLine += ln.slice(6);
      }
      if (!dataLine) continue;
      let payload: any;
      try {
        payload = JSON.parse(dataLine);
      } catch {
        continue;
      }
      switch (event as StreamEventKind) {
        case "intent":
          handlers.onIntent?.(payload);
          break;
        case "prelude":
          handlers.onPrelude?.(payload);
          break;
        case "chunk":
          handlers.onChunk?.(payload.delta ?? "");
          break;
        case "done":
          handlers.onDone?.(payload as ChatAgentResponse);
          return;
        case "error":
          handlers.onError?.(payload?.detail ?? "stream error");
          throw new Error(payload?.detail ?? "stream error");
      }
    }
  }
}
